import { useEphemeralStore } from '@/lib/app-store';
import type { Locale } from '@worldcoo/monolith/core';
import { AnimateNumber } from '@worldcoo/ui/custom/animate-number';
import { useDebounce } from '@worldcoo/ui/hooks/use-debounce';
import { useEffect, useRef, useState } from 'react';

/**
 * Coordinates the bump release across multiple instances.
 * Each instance registers when it goes to 'up', then reports done
 * when its counting finishes. The 400ms hold timer only starts once
 * ALL registered instances have reported — so the bump stays up
 * until the slowest counter finishes.
 */
let _holdTimer: ReturnType<typeof setTimeout> | undefined;
let _holdCallbacks: Array<() => void> = [];
let _expectedBumps = 0;

function tryFlushRelease() {
	if (_expectedBumps > 0 && _holdCallbacks.length >= _expectedBumps) {
		clearTimeout(_holdTimer);
		_holdTimer = setTimeout(() => {
			const cbs = _holdCallbacks;
			_holdCallbacks = [];
			_expectedBumps = 0;
			cbs.forEach((fn) => fn());
		}, 400);
	}
}

function registerBump() {
	_expectedBumps++;
}

function unregisterBump() {
	if (_expectedBumps > 0) _expectedBumps--;
	tryFlushRelease();
}

function scheduleBumpRelease(callback: () => void) {
	_holdCallbacks.push(callback);
	tryFlushRelease();
}

/**
 * A wrapper around AnimateNumber to trigger the bump effect when a donation is made.
 *
 * Uses a three-phase state machine: idle → up (scale 1.5) → down (scale 1) → idle.
 * The bump-up is delayed ~700ms so it reaches full scale right as counting begins
 * (debounce is 1s). After counting, the bump holds briefly before animating down.
 *
 * IMPORTANT: multiple instances share `donationConfirmed`. Each instance's animation
 * runs independently — we never cancel a running animation when `donationConfirmed`
 * goes false (another instance may have cleared it). A `triggeredRef` prevents
 * re-triggering once the cycle has started.
 **/
export function PortalAnimateNumber(props: {
	locale: Locale;
	value: number;
	suffix?: string;
	decimalDigits?: number;
	className?: string;
	testId?: string;
	/** Skip the count-up and settle straight to the value. See `AnimateNumber`. */
	instant?: boolean;
}) {
	const donationConfirmed = useEphemeralStore((store) => store.donationConfirmed);
	const setDonationConfirmed = useEphemeralStore((store) => store.setDonationConfirmed);
	const setDonationToConfirm = useEphemeralStore((store) => store.setDonationToConfirm);
	const debouncedValue = useDebounce(props.value, 1_000);
	const bump = donationConfirmed && props.suffix !== '%';
	const [bumpPhase, setBumpPhase] = useState<'idle' | 'up' | 'down'>('idle');
	const triggeredRef = useRef(false);
	const registeredRef = useRef(false);
	const completedRef = useRef(false);
	const spanRef = useRef<HTMLSpanElement>(null);
	const animRef = useRef<Animation>(undefined);

	// Unmount cleanup: if this instance registered a bump but never reported completion,
	// decrement the shared counter so other counters can release.
	useEffect(() => {
		return () => {
			if (registeredRef.current && !completedRef.current) unregisterBump();
		};
	}, []);

	// Only START the bump on a false→true edge of `bump`.
	// Never cancel a running animation when bump goes false.
	useEffect(() => {
		if (bump && bumpPhase === 'idle' && !triggeredRef.current) {
			// Only bump if the value actually changed (debouncedValue is still old,
			// props.value is new). Counters whose value didn't change (e.g. campaigns
			// count) must not register — otherwise they'd block the release forever
			// since their onAnimateComplete would never fire.
			const valueWillChange = debouncedValue !== props.value;
			if (!valueWillChange) return;

			triggeredRef.current = true;
			const timer = setTimeout(() => {
				registerBump();
				registeredRef.current = true;
				completedRef.current = false;
				setBumpPhase('up');
			}, 700);
			return () => clearTimeout(timer);
		}
		if (!bump) {
			triggeredRef.current = false;
		}
	}, [bump, bumpPhase, debouncedValue, props.value]);

	// Drive animations via Web Animations API
	useEffect(() => {
		const el = spanRef.current;
		if (!el) return;

		if (bumpPhase === 'up') {
			animRef.current?.cancel();
			el.style.display = 'inline-block';
			animRef.current = el.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.5)' }], {
				duration: 300,
				fill: 'forwards',
				easing: 'ease',
			});
		} else if (bumpPhase === 'down') {
			// Bake scale(1.5) into inline style, then cancel the up-animation
			// so the down-animation starts from the committed position
			try {
				animRef.current?.commitStyles();
			} catch {
				/* already idle */
			}
			animRef.current?.cancel();
			animRef.current = el.animate([{ transform: 'scale(1.5)' }, { transform: 'scale(1)' }], {
				duration: 500,
				fill: 'forwards',
				easing: 'ease',
			});
			animRef.current.onfinish = () => {
				animRef.current?.cancel();
				el.style.transform = '';
				el.style.display = '';
				setBumpPhase('idle');
				setDonationConfirmed(false);
				setDonationToConfirm(undefined);
			};
		} else {
			// idle — clean up any leftover animation
			if (animRef.current) {
				animRef.current.cancel();
				animRef.current = undefined;
				el.style.transform = '';
				el.style.display = '';
			}
		}
	}, [bumpPhase, setDonationConfirmed, setDonationToConfirm]);

	return (
		<span ref={spanRef}>
			<AnimateNumber
				locale={props.locale}
				value={donationConfirmed ? debouncedValue : props.value}
				suffix={props.suffix}
				decimalDigits={props.decimalDigits}
				testId={props.testId}
				// A confirmed donation must always COUNT UP to the new figure, even where the caller
				// otherwise wants no text effect: that count-up is the feedback for the donation, and it is
				// what the bump animation above is timed against.
				instant={props.instant && !donationConfirmed}
				onAnimateComplete={() => {
					// `completedRef` is the one-shot latch for this bump cycle (reset to false at registration).
					// `scheduleBumpRelease` pushes onto the shared callback list, and the release fires once
					// that list reaches `_expectedBumps` — so a second report from the same instance would
					// release the bump while a sibling counter is still counting. Reachable now that the
					// counter settles immediately (and reports completion) on every formatting or mode change,
					// not only when its value lands.
					if (bumpPhase === 'up' && !completedRef.current) {
						completedRef.current = true;
						scheduleBumpRelease(() => setBumpPhase('down'));
					}
				}}
			/>
		</span>
	);
}
